import type { ProviderChatThread } from "./types";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { queryClient } from "@/ajax/queryClient";
import { ChatThreadHeader } from "./ChatThreadHeader";
import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { useTRPC } from "@/trpc";
import posthog from "posthog-js";
import { ChatThreadBody } from "./chat-thread-body";
import { addMilliseconds } from "date-fns";
import { getVirtualThreadUserId, isVirtualThreadId } from "./virtual-thread";

interface ThreadViewProps {
  thread: ProviderChatThread;
  onBack: () => void;
  readOnly?: boolean;
  onUnarchive: () => void;
  onRefreshThreads?: () => Promise<void>;
  currentUser?: {
    id: string;
    is_admin?: boolean;
  };
  organizationBusinessUnitId?: string;
  canManageJobPosts?: boolean;
  documentSigningEnabled?: boolean;
}

// Loading state component
const LoadingState = () => (
  <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center overflow-y-auto bg-slate-50 p-4">
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs sm:p-8">
      <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
      <p className="mt-3 text-slate-600 font-medium">Loading messages...</p>
    </div>
  </div>
);

// Error state component
const ErrorState = () => (
  <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center overflow-y-auto bg-slate-50 p-4">
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs sm:p-8 text-center max-w-sm">
      <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="h-8 w-8 text-red-600" />
      </div>
      <h3 className="text-lg font-semibold text-slate-800 mb-2">
        Failed to load messages
      </h3>
      <p className="text-slate-600 mb-4 text-sm leading-relaxed">
        Something went wrong while loading your conversation.
      </p>
      <Button
        variant="outline"
        className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition-all duration-200"
        onClick={() => window.location.reload()}
      >
        Retry
      </Button>
    </div>
  </div>
);

// Main thread view component
export const ChatThreadView: React.FC<ThreadViewProps> = ({
  thread,
  onBack,
  onUnarchive,
  onRefreshThreads,
  readOnly = false,
  currentUser,
  organizationBusinessUnitId,
  canManageJobPosts,
  documentSigningEnabled,
}) => {
  const trpc = useTRPC();
  const router = useRouter();
  const isVirtual = isVirtualThreadId(thread.id);

  const {
    data: messages,
    isLoading,
    error,
  } = useQuery(
    trpc.chat.listChatThreadMessages.queryOptions(
      {
        threadId: thread.id,
      },
      {
        staleTime: 0,
        refetchInterval: (query) => {
          return 5_000 + Math.random() * 5_000;
        },
        // Skip virtual threads (no server row yet) and guard against an
        // empty/unset id, which would otherwise send threadId="" to a uuid
        // column and 500.
        enabled: !isVirtual && !!thread.id,
      },
    ),
  );

  const createThreadMutation = useMutation(
    trpc.chat.orgCreateChatThread.mutationOptions(),
  );

  const updateLastReadMutation = useMutation(
    trpc.chat.updateLastReadAt.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(
            trpc.chat.listChatThreadMessages.queryOptions({
              threadId: thread.id,
            }),
          ),
          queryClient.invalidateQueries(
            trpc.chat.getUnreadMessagesCount.queryOptions({}),
          ),
          onRefreshThreads?.(),
        ]);
      },
    }),
  );

  const sendMessageMutation = useMutation(
    trpc.chat.sendMessage.mutationOptions({
      networkMode: "always",
      onSuccess: async () => {
        posthog.capture("chat_message.send.success");
        await Promise.all([
          queryClient.invalidateQueries(
            trpc.chat.listChatThreadMessages.queryOptions({
              threadId: thread.id,
            }),
          ),
          queryClient.invalidateQueries(
            trpc.chat.getUnreadMessagesCount.queryOptions({}),
          ),
          onRefreshThreads?.(),
        ]);
      },
      onError: () => {
        posthog.capture("chat_message.send.error");
      },
    }),
  );

  useEffect(() => {
    if (isVirtual) {
      return;
    }
    if (
      messages &&
      messages.length > 0 &&
      (!thread.last_read_at ||
        new Date(messages[messages.length - 1].createdAt) >=
          new Date(thread.last_read_at))
    ) {
      updateLastReadMutation.mutate({
        threadId: thread.id,
        lastReadAt: addMilliseconds(
          messages[messages.length - 1].createdAt,
          1,
        ).toISOString(),
      });
    }
  }, [messages]);

  const handleSendMessage = async (
    message: string,
    attachmentIds?: string[],
  ) => {
    let threadId = thread.id;

    if (isVirtual) {
      const virtualUserId = getVirtualThreadUserId(thread.id);
      if (!virtualUserId || !organizationBusinessUnitId) {
        throw new Error(
          "Cannot create chat thread without user or org context",
        );
      }
      const created = await createThreadMutation.mutateAsync({
        userId: virtualUserId,
        organizationBusinessUnitId,
      });
      threadId = created.id;
      // Swap the URL param from virtual:<userId> to the real thread id
      router.navigate({
        // @ts-expect-error - existing dynamic route typing
        search: (prev) => ({
          ...prev,
          thread: threadId,
        }),
      });
    }

    await sendMessageMutation.mutateAsync({
      threadId,
      message,
      attachmentIds,
    });
    if (isVirtual) {
      // Ensure the real thread's messages query is fresh after lazy creation
      await queryClient.invalidateQueries(
        trpc.chat.listChatThreadMessages.queryOptions({ threadId }),
      );
    }
    if (thread.archived_at) {
      onUnarchive();
    }
  };

  const archiveMutation = useMutation(
    trpc.chat.archiveThread.mutationOptions({
      onSuccess: async () => {
        await onRefreshThreads?.();
        onBack();
      },
    }),
  );

  // Called after a form assignment is sent from the composer. Unlike a plain
  // chat message, the assignment may have lazily created the thread (virtual
  // thread promotion), so the returned chatThreadId is the source of truth.
  const handleFormAssignmentSent = async (chatThreadId: string) => {
    await Promise.all([
      queryClient.invalidateQueries(
        trpc.chat.listChatThreadMessages.queryOptions({
          threadId: chatThreadId,
        }),
      ),
      queryClient.invalidateQueries(
        trpc.chat.getUnreadMessagesCount.queryOptions({}),
      ),
      onRefreshThreads?.(),
    ]);
    if (isVirtual && chatThreadId !== thread.id) {
      router.navigate({
        // @ts-expect-error - existing dynamic route typing
        search: (prev) => ({
          ...prev,
          thread: chatThreadId,
        }),
      });
    }
  };

  // Virtual threads have no messages yet — show an empty thread body immediately.
  const effectiveMessages = isVirtual ? [] : messages;

  const provider = thread.user_participants[0];
  const recipientUserId = thread.user_participants[0]?.id;
  const endedRelationshipLookup =
    organizationBusinessUnitId && recipientUserId
      ? { organizationBusinessUnitId, providerUserId: recipientUserId }
      : undefined;
  const providerDisplayName = provider
    ? [provider.first_name, provider.last_name]
        .filter(Boolean)
        .join(" ")
        .trim() || undefined
    : undefined;

  if (!isVirtual && error) {
    return <ErrorState />;
  }
  if (!isVirtual && (isLoading || !messages)) {
    return <LoadingState />;
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-slate-50">
      <ChatThreadHeader
        thread={thread}
        onBack={onBack}
        onArchive={() =>
          archiveMutation.mutate({
            threadId: thread.id,
          })
        }
        onViewDetails={() => {}}
        currentUser={currentUser}
        organizationBusinessUnitId={organizationBusinessUnitId}
        onRefreshThreads={onRefreshThreads}
      />
      <ChatThreadBody
        messages={effectiveMessages ?? []}
        onSendMessage={handleSendMessage}
        readOnly={readOnly}
        threadId={thread.id}
        viewMode={organizationBusinessUnitId ? "org" : "provider"}
        user={
          currentUser ? { is_admin: currentUser.is_admin ?? false } : undefined
        }
        organizationBusinessUnitId={organizationBusinessUnitId}
        recipientUserId={thread.user_participants[0]?.id}
        canManageJobPosts={canManageJobPosts}
        documentSigningEnabled={documentSigningEnabled}
        providerDisplayName={providerDisplayName}
        onFormAssignmentSent={handleFormAssignmentSent}
        endedRelationshipLookup={endedRelationshipLookup}
      />
    </div>
  );
};
